/**
 * An objective's drop-off spot: a marked patch of floor the player throws boxes onto,
 * and an invisible sensor reporting which required objects sit inside it. Enter/leave
 * events go to the store; the pure objectives module scores them.
 */

import { CuboidCollider } from '@react-three/rapier';
import { DoubleSide } from 'three';
import type { DropZone as DropZoneData } from '../game/profile/buildGeometry';
import { useGameStore } from '../store/gameStore';
import type { BodyUserData } from './useInteractionTarget';

type Props = {
  zone: DropZoneData;
  color: string;
};

function carryableId(data: unknown): string | null {
  const { kind, id } = (data ?? {}) as Partial<BodyUserData>;
  return kind === 'carryable' && typeof id === 'string' ? id : null;
}

export function DropZone({ zone, color }: Props) {
  const { center, size } = zone.box;

  return (
    <>
      {/* The marker: a bright patch just above the floor, readable at a glance. */}
      <mesh position={[center.x, 0.012, center.z]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[size.x, size.z]} />
        <meshLambertMaterial color={color} side={DoubleSide} />
      </mesh>

      <CuboidCollider
        sensor
        args={[size.x / 2, size.y / 2, size.z / 2]}
        position={[center.x, center.y, center.z]}
        onIntersectionEnter={({ other }) => {
          const id = carryableId(other.rigidBody?.userData);
          if (id) {
            useGameStore.getState().reportZoneEvent(zone.objectiveId, { type: 'enter', objectId: id });
          }
        }}
        onIntersectionExit={({ other }) => {
          const id = carryableId(other.rigidBody?.userData);
          if (id) {
            useGameStore.getState().reportZoneEvent(zone.objectiveId, { type: 'leave', objectId: id });
          }
        }}
      />
    </>
  );
}
